import { NgClass } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileUploadComponent } from 'components/file-upload/file-upload.component';
import { SpinnerComponent } from 'components/spinner/spinner.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import {
  FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE
} from 'types/information-request.interface';
import type {
  BackofficeInformationRequestCard,
  BackofficeInformationRequestCommunicationCard,
  RequestAdditionalInformationForInformationRequestRequest
} from 'types/information-request.interface';
import { getControlsWithErrors, MAX_LENGTH_ADDITIONAL_INFO } from 'util/form.utility';
import { BaseFormModalComponent } from '../base-form-modal.component';

@Component({
  selector: 'app-ir-request-info-modal',
  standalone: true,
  imports: [TranslateModule, SpinnerComponent, NgClass, TooltipComponent, ReactiveFormsModule, FileUploadComponent],
  templateUrl: './ir-request-info-modal.component.html',
  styleUrls: ['./ir-request-info-modal.component.scss', '../../form/base-form.scss']
})
export class IrRequestInfoModalComponent extends BaseFormModalComponent implements AfterViewInit, OnChanges {
  public readonly maxFieldLength = MAX_LENGTH_ADDITIONAL_INFO;
  @Input() isOpen!: boolean;
  @Input() informationRequest!: BackofficeInformationRequestCard;
  @Output() update: EventEmitter<BackofficeInformationRequestCommunicationCard> =
    new EventEmitter<BackofficeInformationRequestCommunicationCard>();
  @ViewChild('content') content!: TemplateRef<any>;
  isSubmittingFiles = false;
  note: BackofficeInformationRequestCommunicationCard | null = null;
  override fileUploadComponentCount = 1;
  readonly communicationFileType = FILE_OBJECT_TYPE_INFO_REQUEST_COMMUNICATION_BACKOFFICE;
  isCrudLoading = false;
  shouldRenderTooltip = false;

  get backofficeText(): AbstractControl<any, any> | null {
    return this.form.get('backofficeText');
  }

  constructor(
    formBuilder: FormBuilder,
    config: NgbModalConfig,
    modalService: NgbModal,
    toastService: ToastService,
    translateService: TranslateService,
    private apiService: ApiService
  ) {
    super(formBuilder, config, modalService, toastService, translateService);
  }

  ngAfterViewInit(): void {
    if (this.isOpen) {
      this.fillForm();
      this.open(this.content);
    }
  }

  override ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.fillForm();
      this.shouldRenderTooltip = false;
      this.open(this.content);
      setTimeout(() => {
        this.shouldRenderTooltip = true;
      }, 10);
    }
  }

  fillForm(): void {
    this.form = this.formBuilder.group({
      backofficeText: ['', [Validators.required, Validators.maxLength(MAX_LENGTH_ADDITIONAL_INFO)]]
    });
  }

  protected override close(): void {
    super.close();
  }

  submit(): void {
    if (this.form.valid) {
      this.isCrudLoading = true;
      const args: RequestAdditionalInformationForInformationRequestRequest = {
        informationRequestId: this.informationRequest.id,
        backofficeText: this.form.value.backofficeText
      };

      this.apiService.createIrRequestInfo(args).subscribe({
        next: (response) => {
          this.toastService.showSuccess('requestAppClientNoteSuccess', '', {
            shouldTranslate: true
          });
          this.note = response;
          this.isSubmittingFiles = true;
          this.isCrudLoading = false;
        },
        error: () => {
          this.toastService.showRequestError();
          this.isCrudLoading = false;
          this.close();
        }
      });
    } else {
      this.submitAttempted = true;
      this.toastService.showFormValidationError(getControlsWithErrors(this.form), 'components.requestAdditionalNotes');
    }
  }

  protected override onSubmitSuccess(): void {
    this.isSubmittingFiles = false;
    this.fileActions = [];
    this.uploadComponentsReceived = 0;
    this.close();

    if (this.note) {
      this.update.emit(this.note);
    }
  }
}
